import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Spinner } from '../common/Spinner';
import { validatePersonaName, normalizePersonaName, finalizePersonaName } from '../../utils/validationUtils';

interface SaveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, description: string, isPublic: boolean, forceNew: boolean) => Promise<void>;
  isLoading: boolean;
  initialName?: string;
  initialDescription?: string;
  currentPersonaId?: string | null;
  suggestedNames?: string[];
}

export type SaveDialogRef = {
  open: () => void;
};

const SaveDialog = forwardRef(function SaveDialogComponent(
  props: SaveDialogProps,
  ref: React.ForwardedRef<SaveDialogRef>
) {
  const {
    isOpen,
    onClose,
    onSave,
    isLoading,
    initialName = '',
    initialDescription = '',
    currentPersonaId,
    suggestedNames = [],
  } = props;

  const { darkMode } = useTheme();
  const [dialogOpen, setDialogOpen] = useState(isOpen);
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [isPublic, setIsPublic] = useState(false);
  const [saveAsNew, setSaveAsNew] = useState(false);
  const [selectedNameOption, setSelectedNameOption] = useState('custom');
  const [nameValidation, setNameValidation] = useState({ valid: true, message: '' });
  const [isComposing, setIsComposing] = useState(false);

  // Derived: are we in "new persona" mode?
  const isNewMode = !currentPersonaId || saveAsNew;

  // Validate name only when it's editable (new mode)
  useEffect(() => {
    if (isNewMode) {
      setNameValidation(validatePersonaName(name));
    } else {
      setNameValidation({ valid: true, message: '' });
    }
  }, [name, isNewMode]);

  // Sync open state
  useEffect(() => {
    setDialogOpen(isOpen);
  }, [isOpen]);

  // Reset form when dialog opens
  useEffect(() => {
    if (dialogOpen) {
      setName(initialName);
      setDescription(initialDescription);
      setSaveAsNew(false);
      setSelectedNameOption('custom');
      setIsPublic(false);
    }
  }, [dialogOpen, initialName, initialDescription]);

  // When user ticks "Save as new", clear the name so they must pick/type one
  const handleSaveAsNewToggle = (checked: boolean) => {
    setSaveAsNew(checked);
    if (checked) {
      setName('');
      setSelectedNameOption('custom');
    } else {
      // Restore the original name
      setName(initialName);
    }
  };

  const handleNameSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value !== 'custom') setName(value);
    setSelectedNameOption(value);
  };

  useImperativeHandle(ref, () => ({
    open: () => setDialogOpen(true),
  }));

  // MutationObserver bridge (kept for backward compat)
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-open' &&
          (mutation.target as HTMLElement).getAttribute('data-open') === 'true'
        ) {
          setDialogOpen(true);
          (mutation.target as HTMLElement).removeAttribute('data-open');
        }
      });
    });
    const el = document.getElementById('save-dialog');
    if (el) observer.observe(el, { attributes: true });
    return () => observer.disconnect();
  }, []);

  if (!dialogOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isNewMode) {
      const validation = validatePersonaName(name);
      if (!validation.valid) {
        setNameValidation(validation);
        return;
      }
    }
    await onSave(name, description, isPublic, saveAsNew);
  };

  const isSubmitDisabled = isLoading || (isNewMode && !nameValidation.valid);

  return (
    <div
      id="save-dialog"
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${
        dialogOpen ? '' : 'hidden'
      }`}
    >
      <div
        className={`p-6 rounded-lg shadow-lg ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        } w-[500px] max-w-full`}
      >
        <h3 className="text-lg font-semibold mb-4">
          {!currentPersonaId
            ? 'Save New Persona'
            : saveAsNew
              ? 'Save as New Persona'
              : 'Update Persona'}
        </h3>

        <form onSubmit={handleSubmit}>

          {/* ── Name section ─────────────────────────────────────────────── */}
          <div className="mb-4">
            {isNewMode ? (
              /* New / Save-as-new: full name picker */
              <>
                <label className="block mb-2 font-medium">Name:</label>

                {suggestedNames.length > 0 && (
                  <div className="mb-2">
                    <select
                      className={`w-full p-2 rounded-md ${
                        darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'
                      }`}
                      value={selectedNameOption}
                      onChange={handleNameSelection}
                    >
                      <option value="custom">Custom name…</option>
                      {suggestedNames.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select a suggested name or enter your own below
                    </p>
                  </div>
                )}

                <input
                  type="text"
                  className={`w-full p-2 rounded-md ${
                    darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'
                  } ${
                    !nameValidation.valid
                      ? darkMode ? 'border border-red-500' : 'border-2 border-red-500'
                      : ''
                  }`}
                  value={name}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const next = isComposing ? raw : normalizePersonaName(raw);
                    setName(next);
                    setSelectedNameOption('custom');
                  }}
                  onCompositionStart={() => setIsComposing(true)}
                  onCompositionEnd={(e) => {
                    setIsComposing(false);
                    setName(finalizePersonaName(e.currentTarget.value));
                  }}
                  onBlur={(e) => {
                    // Finalize on blur — strip edge underscores that the live
                    // transform intentionally preserved so the user could
                    // build '_Foo' / 'Foo_Bar' left-to-right.
                    setName(finalizePersonaName(e.currentTarget.value));
                  }}
                  autoCapitalize="off"
                  autoCorrect="off"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="Enter a name for this persona"
                  required
                  autoFocus
                />

                {nameValidation.message && (
                  <p className={`text-xs mt-1 ${!nameValidation.valid ? 'text-red-500' : 'text-gray-500'}`}>
                    {nameValidation.message}
                  </p>
                )}

                <p className="text-xs text-gray-500 mt-1">
                  Use letters, numbers, and underscores (up to 80). Spaces and other characters become <code>_</code> as you type — e.g., <code>Creative Writing Coach</code> → <code>Creative_Writing_Coach</code>. Latin alphabet only.
                </p>
              </>
            ) : (
              /* Update mode: name is locked — just show it */
              <>
                <label className="block mb-1 font-medium">Name:</label>
                <div className={`w-full p-2 rounded-md flex items-center justify-between ${
                  darkMode ? 'bg-gray-700/50 text-gray-300' : 'bg-gray-100 text-gray-600'
                }`}>
                  <span className="font-mono text-sm">{name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500'
                  }`}>locked</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  To save under a different name, use "Save as new persona" below.
                </p>
              </>
            )}
          </div>

          {/* ── Description ──────────────────────────────────────────────── */}
          <div className="mb-4">
            <label className="block mb-2 font-medium">Description:</label>
            <textarea
              className={`w-full p-2 rounded-md ${
                darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'
              }`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter a description (optional)"
              rows={3}
            />
          </div>

          {/* ── Save as new (only when updating an existing persona) ──────── */}
          {currentPersonaId && (
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveAsNew}
                  onChange={(e) => handleSaveAsNewToggle(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Save as new persona instead of updating</span>
              </label>
            </div>
          )}

          {/* ── Public toggle ─────────────────────────────────────────────── */}
          <div className="mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Make this persona public</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-5">
              Public personas can be viewed and used by all users
            </p>
          </div>

          {/* ── Actions ──────────────────────────────────────────────────── */}
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={`px-4 py-2 rounded-md ${
                darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className={`px-4 py-2 rounded-md text-white ${
                darkMode ? 'bg-green-600 hover:bg-green-700' : 'bg-green-500 hover:bg-green-600'
              } ${isSubmitDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isLoading
                ? <Spinner size="sm" />
                : !currentPersonaId
                  ? 'Save'
                  : saveAsNew
                    ? 'Save as New'
                    : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default SaveDialog;
